import { baseApi } from './baseApi';

const TAG = 'HealthCalculators';
const TAG_MENTAL = 'MentalHealthQuestionnaires';
const TAG_BP = 'BloodPressureReadings';

/** Fixed query arg: stable RTK Query cache key + full list (backend often defaults to limit=5). */
export const FETCH_BLOOD_PRESSURE_READINGS_ARG = { limit: 500 };

export function normalizeBloodPressureReadingsList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  return [];
}

const HealthCalculatorsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchCalculatorDashboard: builder.query({
      query: ({ language = 'pl' } = {}) => ({
        url: `/health-calculators/dashboard_summary/?language=${language}`,
        method: 'GET',
      }),
      providesTags: [TAG],
    }),

    fetchCalculatorDescriptions: builder.query({
      query: ({ language = 'pl' } = {}) => ({
        url: `/health-calculators/descriptions/?language=${language}`,
        method: 'GET',
      }),
    }),

    fetchAvailableCalculators: builder.query({
      query: () => ({
        url: '/health-calculators/available/',
        method: 'GET',
      }),
      providesTags: [TAG],
    }),

    fetchCalculatorHistory: builder.query({
      query: ({ calculator_type, ordering = '-calculated_at', limit = 100, offset } = {}) => {
        const params = new URLSearchParams();
        // calculator_type can be a string or an array of strings
        if (Array.isArray(calculator_type)) {
          calculator_type.forEach((t) => params.append('calculator_type', t));
        } else if (calculator_type) {
          params.set('calculator_type', calculator_type);
        }
        params.set('ordering', ordering);
        params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        return {
          url: `/health-calculators/?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response) => {
        if (Array.isArray(response)) return response;
        return response?.results ?? [];
      },
      providesTags: [TAG],
    }),

    runCalculator: builder.mutation({
      // Always returns a preview (200 OK); result is NOT persisted to history.
      // If `inputs` is undefined/empty, the backend uses the user's profile data (auto-fill).
      query: ({ calculator_type, inputs }) => ({
        url: '/health-calculators/calculate/',
        method: 'POST',
        body: inputs ? { calculator_type, inputs } : { calculator_type },
      }),
    }),

    updateMedicalData: builder.mutation({
      query: (body) => ({
        url: '/health-interview/update_medical_data/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PreventionShield'],
    }),

    fetchMentalHealthHistory: builder.query({
      query: ({ limit = 100 } = {}) => ({
        url: limit ? `/mental-health-questionnaires/?limit=${limit}` : '/mental-health-questionnaires/',
        method: 'GET',
      }),
      transformResponse: (response) => {
        if (Array.isArray(response)) return response;
        return response?.results ?? [];
      },
      providesTags: [TAG_MENTAL],
    }),

    fetchQuestionnaireQuestions: builder.query({
      query: ({ type, language = 'pl' }) => ({
        url: `/mental-health-questionnaires/questions/?type=${type}&language=${language}`,
        method: 'GET',
      }),
    }),

    submitQuestionnaire: builder.mutation({
      query: (body) => ({
        url: '/mental-health-questionnaires/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_MENTAL, TAG, 'PreventionShield'],
    }),

    patchCalculatorResult: builder.mutation({
      query: ({ uuid, ...body }) => ({
        url: `/health-calculators/${uuid}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [TAG, 'PreventionShield'],
    }),

    deleteCalculatorResult: builder.mutation({
      query: ({ uuid }) => ({
        url: `/health-calculators/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG, 'PreventionShield'],
    }),

    // Blood pressure readings (SCORE2 — separate model, not in health interview)
    // Backend paginates (e.g. default limit=5) — always pass a limit (e.g. FETCH_BLOOD_PRESSURE_READINGS_ARG).
    fetchBloodPressureReadings: builder.query({
      query: (arg = {}) => ({
        url: `/blood-pressure-readings/?limit=${arg.limit ?? 500}`,
        method: 'GET',
      }),
      transformResponse: (response) => normalizeBloodPressureReadingsList(response),
      providesTags: [TAG_BP, TAG],
    }),

    fetchBloodPressureReading: builder.query({
      query: (id) => ({
        url: `/blood-pressure-readings/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_BP, id }],
    }),

    addBloodPressureReading: builder.mutation({
      query: (body) => ({
        url: '/blood-pressure-readings/',
        method: 'POST',
        body: {
          date: body.date,
          systolic: body.systolic,
          diastolic: body.diastolic,
        },
      }),
      invalidatesTags: [TAG_BP, TAG, 'PreventionShield'],
    }),
  }),
});

export const {
  useFetchCalculatorDashboardQuery,
  useFetchCalculatorDescriptionsQuery,
  useFetchAvailableCalculatorsQuery,
  useFetchCalculatorHistoryQuery,
  useRunCalculatorMutation,
  useUpdateMedicalDataMutation,
  useFetchMentalHealthHistoryQuery,
  useFetchQuestionnaireQuestionsQuery,
  useSubmitQuestionnaireMutation,
  usePatchCalculatorResultMutation,
  useDeleteCalculatorResultMutation,
  useFetchBloodPressureReadingsQuery,
  useFetchBloodPressureReadingQuery,
  useAddBloodPressureReadingMutation,
} = HealthCalculatorsApi;
